import type { Role } from '@chatapp-inbox/shared';
import { isApiError } from '../api';

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrador',
  agent: 'Atendente',
};

const plural = (n: number, singular: string, pluralForm: string): string =>
  `${n} ${n === 1 ? singular : pluralForm}`;

/**
 * "1 segundo", "45 segundos", "2 minutos", "2 horas". Arredonda PARA CIMA: dizer "1 minuto" para
 * uma espera de 61 s faria a pessoa tentar cedo demais e esbarrar no bloqueio de novo.
 * Devolve null quando não há um tempo utilizável (sem número, zero ou negativo).
 */
export function formatWait(seconds: number | null): string | null {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 60) return plural(Math.ceil(seconds), 'segundo', 'segundos');
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 120) return plural(minutes, 'minuto', 'minutos');
  return plural(Math.ceil(seconds / 3600), 'hora', 'horas');
}

function tooManyAttempts(retryAfterSeconds: number | null): string {
  const wait = formatWait(retryAfterSeconds);
  return `Muitas tentativas de entrada. Tente novamente ${wait ? `em ${wait}` : 'em instantes'}.`;
}

export const MISSING_CREDENTIALS = 'Informe o e-mail e a senha.';
export const SESSION_EXPIRED = 'Sua sessão expirou. Entre novamente.';

export interface LoginFailure {
  message: string;
  /** A senha só é apagada quando o servidor a recusou; nos demais erros a pessoa não perde o que digitou. */
  credentialsRefused: boolean;
}

export function describeLoginError(err: unknown): LoginFailure {
  if (isApiError(err)) {
    if (err.code === 'invalid_credentials') {
      return { message: 'E-mail ou senha incorretos.', credentialsRefused: true };
    }
    if (err.status === 429) {
      return { message: tooManyAttempts(err.retryAfterSeconds), credentialsRefused: false };
    }
    return { message: err.message, credentialsRefused: false };
  }
  return { message: 'Não foi possível entrar. Tente de novo.', credentialsRefused: false };
}

export function describeLogoutError(err: unknown): string {
  return `Não foi possível sair. ${isApiError(err) ? err.message : 'Tente de novo.'}`;
}
